import { ExpenseRequestTypeEnum, RolesEnum } from "@finway-group/shared/lib/models"
import { Dropdown, Menu } from "antd"
import Icon from "Components/icon"
import EmployeeFormModal from "Components/modals/employee.modal"
import ExpenseCreateFormModal from "Components/modals/expenseCreateForm.modal"
import VendorFormModal from "Components/modals/vendor.modal"
import CostCenterFormModal from "Features/pages/costCenters/costCenterForm/CostCenterFormModal"
import { isRequestActionDisabled } from "Features/pages/expenses/expenseUtils"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useModal } from "Shared/context/modal.context"
import { useCompany } from "Shared/hooks/company.hooks"
import { useCostCenters } from "Shared/hooks/costCenter.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { useVendors } from "Shared/hooks/vendor.hooks"
import { UserService } from "Shared/services"
import { getPopupAnchor, insertIf } from "Shared/utils"
import { MenuItemInterface, NavbarMenuItemEnum } from "./types"

const ActionMenuNavbar: React.FC<{}> = () => {
    const { t } = useTranslation()

    const [isExpenseModalShowing, setIsExpenseModalShowing] = useState(false)
    const [isVendorModalShowing, setIsVendorModalShowing] = useState<boolean>(false)
    const [isCostCenterModalShowing, setIsCostCenterModalShowing] = useState<boolean>(false)

    const [requestType, setRequestType] = useState<ExpenseRequestTypeEnum>(ExpenseRequestTypeEnum.PURCHASE)
    const [isReimbursement, setIsReimbursement] = useState<boolean>(false)
    const { showModal } = useModal()

    const loggedInUser = useLoggedInEmployeeProfile()
    const loggedInUserRole = UserService.getEmployeeProfile(loggedInUser.id).activeCompanyProfile?.role
    const { isVendorCreationRestricted } = useCompany()

    const employees = useEmployees(true)
    const vendors = useVendors(true)
    const costCenters = useCostCenters(true)
    const requestButtonsDisabled = isRequestActionDisabled(loggedInUser, vendors, costCenters, employees)
    const shouldShowVendorButton = loggedInUser.activeCompanyProfile?.isAdmin || !isVendorCreationRestricted

    const actionMenuItems: MenuItemInterface[] = [
        ...insertIf(!requestButtonsDisabled, {
            link: "purchase_request",
            label: t("label:purchase_request"),
            canView: [RolesEnum.ADMIN, RolesEnum.APPROVER, RolesEnum.EMPLOYEE],
        }),
        ...insertIf(!requestButtonsDisabled, {
            link: "invoice_request",
            label: t("label:invoice_request"),
            canView: [RolesEnum.ADMIN, RolesEnum.APPROVER, RolesEnum.EMPLOYEE],
        }),
        ...insertIf(!requestButtonsDisabled, {
            link: "reimbursement_request",
            label: t("label:reimbursement_request"),
            canView: [RolesEnum.ADMIN, RolesEnum.APPROVER, RolesEnum.EMPLOYEE],
        }),
        {
            link: "employee",
            label: t("label:employee"),
            canView: [RolesEnum.ADMIN],
        },
        {
            link: "costCenter",
            label: t("label:cost_center"),
            canView: [RolesEnum.ADMIN],
        },
        ...insertIf(shouldShowVendorButton, {
            link: "vendor",
            label: t("label:vendor"),
            canView: [RolesEnum.ADMIN, RolesEnum.APPROVER, RolesEnum.EMPLOYEE],
        }),
    ]

    const handleCreateMenuCommand = (command: string): void => {
        setIsReimbursement(command === NavbarMenuItemEnum.REIMBURSEMENT_REQUEST)

        switch (command) {
            case NavbarMenuItemEnum.PURCHASED_REQUEST:
                setRequestType(ExpenseRequestTypeEnum.PURCHASE)
                setIsExpenseModalShowing(true)
                break
            case NavbarMenuItemEnum.INVOICE_REQUEST:
                setRequestType(ExpenseRequestTypeEnum.INVOICE)
                setIsExpenseModalShowing(true)
                break
            case NavbarMenuItemEnum.REIMBURSEMENT_REQUEST:
                setRequestType(ExpenseRequestTypeEnum.REIMBURSEMENT)
                setIsExpenseModalShowing(true)
                break
            case NavbarMenuItemEnum.EMPLOYEE:
                showModal(EmployeeFormModal, false, { isShowing: true, isNew: true })
                break
            case NavbarMenuItemEnum.COST_CENTER:
                setIsCostCenterModalShowing(true)
                break
            case NavbarMenuItemEnum.VENDOR:
                setIsVendorModalShowing(true)
                break
            default:
                break
        }
    }

    const actionMenu = (
        <Menu className="z-20 truncate max-w-200">
            {actionMenuItems
                .filter((menuItem) => menuItem.canView && menuItem.canView.indexOf(loggedInUserRole) > -1)
                .map(({ link, label }: MenuItemInterface, index: number) => (
                    <Menu.Item key={index} className="min-w-180 w-180 " onClick={() => handleCreateMenuCommand(link)}>
                        {label}
                    </Menu.Item>
                ))}
        </Menu>
    )

    return (
        <>
            <ExpenseCreateFormModal
                isShowing={isExpenseModalShowing}
                isNew={true}
                type={requestType}
                onCancel={() => setIsExpenseModalShowing(false)}
                isReimbursement={isReimbursement}
            />
            <CostCenterFormModal isNew={true} isShowing={isCostCenterModalShowing} onHide={() => setIsCostCenterModalShowing(false)} />

            <VendorFormModal isNew={true} isShowing={isVendorModalShowing} onCancel={setIsVendorModalShowing} />

            <Dropdown className={"flex items-center"} trigger={["click"]} overlay={actionMenu} getPopupContainer={getPopupAnchor()}>
                <Icon className="cursor-pointer mr-20" icon="add_circle" />
            </Dropdown>
        </>
    )
}

export default ActionMenuNavbar
